import React, { useEffect, useState } from "react";
import ChangeRole from "./ChangeRole"; // Import the ChangeRole component
import AddTeamMembers from "./AddTeamMembers"; // Import AddTeamMembers component
import {
  Box,
  Typography,
  Button,
  Avatar,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Skeleton,
  Dialog,
  Tooltip,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Search as SearchIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTeamMembers,
  deleteTeamMember,
} from "../../../redux/Slice/Settings/settingsSlice";
import {
  clearSuccessMessage,
} from "../../../redux/Slice/Settings/addfunctionsSlice";
import AuthView from "../../../utils/AuthView";
import { fetchUserPermissions } from "../../../redux/Slice/User/userDetailsSlice";
import { toast } from "react-hot-toast";
import { checkBeforeOutbound, checkSubscriptionActive, isSubscriptionBlockedError } from "../../../utils/subscriptionError";
import { getAuthToken } from "../../../utils/getAuthToken";

const TeamMembers = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
 // 1536px and above
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 0-599px
  const { teamMembers, loading, error } = useSelector(
    (state) => state.settings
  );

  const TeamMemberPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.TeamMember || {}
  );

  const { successMessage: addMemberSuccess } = useSelector(
    (state) => state.addFunctions
  );

  const [addMemberToast, setAddMemberToast] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (addMemberSuccess) {
      setAddMemberToast({ open: true, message: addMemberSuccess, severity: "success" });
      const t = setTimeout(() => dispatch(clearSuccessMessage()), 4000);
      return () => clearTimeout(t);
    }
  }, [addMemberSuccess, dispatch]);

  const [showAddMember, setShowAddMember] = useState(false); // Toggle for AddTeamMembers component
  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage, setMembersPerPage] = useState(10); // Default to 10 for all screens
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null); // Stores the current role
  const [selectedId, setSelectedId] = useState(null); // Stores the current role
  const [searchQuery, setSearchQuery] = useState(""); // Add search query state

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(fetchTeamMembers());
  }, [dispatch]);

  // Filter members based on search query
  const filteredMembers = teamMembers.filter((member) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      member.name.toLowerCase().includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower) ||
      (member.Roles?.roleName || "").toLowerCase().includes(searchLower)
    );
  });

  if (TeamMemberPermissions.View === false) {
    return (
      <Box
        sx={{
          padding: "20px",
          height: "100%",
          borderRadius: "8px",
          backgroundColor: "background.paper",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          display: "flex",
          flexDirection: "column",
          color: "text.primary",
          position: "relative",
        }}
      >
        <Typography variant="h5" sx={{ marginBottom: "2rem" }}>
          Team Members
        </Typography>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            borderRadius: "8px",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          }}
        >
          <AuthView />
        </Box>
      </Box>
    );
  }

  const handleAddMemberClick = async () => {
    const { blocked, message } = await checkBeforeOutbound(getAuthToken, ['teamMembersCreated']);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    setShowAddMember(true);
  };

  const handleBackFromAddMember = () => {
    setShowAddMember(false); // Return to TeamMembers component
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prev) => prev + direction);
  };

  const handleRowsPerPageChange = (event) => {
    setMembersPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (_id) => {
    const { blocked, message } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
    dispatch(deleteTeamMember(_id))
      .unwrap()
      .then(() => {
        dispatch(fetchTeamMembers());
        toast.success("Team member deleted successfully.");
      })
      .catch((error) => {
        if (!isSubscriptionBlockedError(error)) {
          toast.error(typeof error === 'string' ? error : "Failed to delete team member.");
        }
      });
  };

  const handleRoleChange = (newRole) => {};

  const indexOfLastMember = currentPage * membersPerPage;
  const indexOfFirstMember = indexOfLastMember - membersPerPage;
  const currentMembers = filteredMembers.slice(
    indexOfFirstMember,
    indexOfLastMember
  );

  return (
    <Box
      sx={{
        paddingTop: { xs: "8px", sm: "16px", md: "20px", lg: "20px", xl: "20px" },
        paddingLeft: { xs: "4px", sm: "16px", md: "20px", lg: "20px", xl: "20px" },
        paddingRight: { xs: "4px", sm: "16px", md: "20px", lg: "20px", xl: "20px" },
        paddingBottom: {
          xs: "calc(10px + env(safe-area-inset-bottom, 0px))",
          sm: "calc(12px + env(safe-area-inset-bottom, 0px))",
          md: "20px",
          lg: "20px",
          xl: "20px",
        },
        marginBottom: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
        height: { xs: "auto", sm: "auto", md: "100%", lg: "100%", xl: "100%" },
        minHeight: 0,
        flex: { xs: "0 1 auto", sm: "0 1 auto", md: 1, lg: 1, xl: 1 },
        alignSelf: "stretch",
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        color: "text.primary",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: { xs: "0.5rem", sm: "1rem", md: "1.5rem", lg: "2rem", xl: "2rem" },
          gap: { xs: 0.5, sm: 1.5, md: 2, lg: 2, xl: 2 },
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search members..."
          variant="outlined"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1); // Reset to first page when searching
          }}
          size="small"
          sx={{
            flex: 1,
            fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem", lg: "1rem", xl: "1rem" },
            "& .MuiInputLabel-root": {
              color: "text.secondary",
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem", lg: "1rem", xl: "1rem" },
            },
            "& .MuiInputBase-input": {
              color: "text.primary",
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem", lg: "1rem", xl: "1rem" },
              padding: { xs: "6px 8px", sm: "10px 14px", md: "14px", lg: "14px", xl: "14px" },
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.23)" 
                  : "rgba(0, 0, 0, 0.23)",
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.5)" 
                  : "rgba(0, 0, 0, 0.5)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: { xs: "0.875rem", sm: "1.25rem", md: "1.5rem", lg: "1.5rem", xl: "1.5rem" } }} />
              </InputAdornment>
            ),
          }}
        />
        <Tooltip
          title={
            !TeamMemberPermissions.Add ? "You don't have permission to add team members" : ""
          }
        >
          <span>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon sx={{ fontSize: { xs: "0.875rem", sm: "1.1rem", md: "1.25rem", lg: "1.25rem", xl: "1.25rem" } }} />}
              onClick={handleAddMemberClick}
              disabled={!TeamMemberPermissions.Add}
              size="small"
              sx={{
                borderColor: "primary.main",
                color: "primary.main",
                borderRadius: "0.8rem",
                padding: { xs: "2px 4px", sm: "6px 12px", md: "0.5rem 1rem", lg: "0.5rem 1rem", xl: "0.5rem 1rem" },
                fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                textTransform: "none",
                marginLeft: { xs: 0.5, sm: 1.5, md: 2, lg: 2, xl: 2 },
                whiteSpace: { xs: "nowrap", sm: "normal", md: "normal", lg: "normal", xl: "normal" },
                "&:hover": {
                  backgroundColor: "rgba(14, 144, 70, 0.08)",
                  borderColor: "primary.main",
                },
                "&.Mui-disabled": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              Add Team Member
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Members Table */}
      <TableContainer
        component={Paper}
        sx={{
          flex: { xs: "0 1 auto", sm: "0 1 auto", md: 1, lg: 1, xl: 1 },
          minHeight: 0,
          maxHeight: {
            xs: "calc(100dvh - 200px)",
            sm: "calc(100dvh - 220px)",
            md: "100%",
            lg: "100%",
            xl: "100%",
          },
          boxShadow: "none",
          backgroundColor: "background.paper",
          overflow: "auto",
          // Decrease scrollbar size for all screen sizes
          scrollbarWidth: { xs: "thin", sm: "thin", md: "thin", lg: "thin", xl: "thin" }, // Firefox
          "&::-webkit-scrollbar": {
            width: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Vertical scrollbar - very thin for md/lg/xl
            height: { xs: "4px", sm: "4px", md: "3px", lg: "3px", xl: "3px" }, // Horizontal scrollbar - very thin for md/lg/xl
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#555" : "#888",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#777" : "#555",
            },
          },
          border: (theme) => theme.palette.mode === "dark" 
            ? "1px solid rgba(255, 255, 255, 0.12)" 
            : "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "1rem",
          "& .MuiTableCell-root": {
            borderBottom: (theme) => theme.palette.mode === "dark" 
              ? "1px solid rgba(255, 255, 255, 0.12)" 
              : "1px solid rgba(0, 0, 0, 0.12)",
            color: "text.primary",
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            padding: { xs: "4px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "background.paper",
            color: "text.primary",
            fontWeight: "bold",
          },
          "& .MuiTableRow-root:hover": {
            backgroundColor: (theme) => theme.palette.mode === "dark" 
              ? "rgba(255, 255, 255, 0.08)" 
              : "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "6px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Member
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "6px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "6px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Role
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  backgroundColor: "background.paper",
                  color: "text.primary",
                  padding: { xs: "6px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" },
                  fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem", lg: "0.9rem", xl: "0.9rem" },
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Skeleton
                    variant="rectangular"
                    height={50}
                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                  />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ color: "error.main" }}>
                  {error}
                </TableCell>
              </TableRow>
            ) : (
              currentMembers.map((member) => {
                const roleName = member.Roles?.roleName || "Unknown";

                return (
                  <TableRow
                    key={member._id || member.id || member.email}
                    sx={{
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <TableCell sx={{ padding: { xs: "4px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: "#8EA7FF",
                            color: "common.black",
                            mr: { xs: 0.5, sm: 1.5, md: 2, lg: 2, xl: 2 },
                            width: { xs: 24, sm: 32, md: 40, lg: 40, xl: 40 },
                            height: { xs: 24, sm: 32, md: 40, lg: 40, xl: 40 },
                            fontSize: { xs: "0.65rem", sm: "0.875rem", md: "1rem", lg: "1rem", xl: "1rem" },
                          }}
                        >
                          {member.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              fontWeight: "bold", 
                              color: "text.primary",
                              fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                            }}
                          >
                            {member.name}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ fontSize: { xs: "0.6rem", sm: "0.7rem", md: "0.75rem", lg: "0.75rem", xl: "0.75rem" } }}
                          >
                            {member.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ padding: { xs: "4px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "0.55rem", sm: "0.8rem", md: "0.875rem", lg: "0.875rem", xl: "0.875rem" },
                          color:
                            member.status === "Active"
                              ? "success.main"
                              : member.status === "Pending"
                              ? "warning.main"
                              : "error.main",
                        }}
                      >
                        {member.status}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ padding: { xs: "4px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      {roleName === "Owner" ? (
                        <Tooltip title="You can't change Owner roles" arrow>
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<EditIcon sx={{ fontSize: { xs: "0.65rem", sm: "1rem", md: "1.125rem", lg: "1.125rem", xl: "1.125rem" } }} />}
                              sx={{
                                borderRadius: "1rem",
                                fontWeight: "bold",
                                textTransform: "capitalize",
                                backgroundColor: "#1BB5EC",
                                color: "common.white",
                                padding: { xs: "2px 4px", sm: "6px 10px", md: "8px 12px", lg: "8px 12px", xl: "8px 12px" },
                                fontSize: { xs: "0.55rem", sm: "0.75rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem" },
                                minWidth: { xs: "50px", sm: "70px", md: "80px", lg: "80px", xl: "80px" },
                                "& .MuiButton-startIcon": {
                                  marginRight: { xs: "1px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
                                  marginLeft: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
                                  "& > *:nth-of-type(1)": {
                                    fontSize: { xs: "0.65rem", sm: "1rem", md: "1.125rem", lg: "1.125rem", xl: "1.125rem" },
                                  },
                                },
                                "&:hover": {
                                  backgroundColor: "#1BB5EC",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "#1BB5EC",
                                  color: "common.white",
                                },
                              }}
                              disabled
                            >
                              {roleName}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            !TeamMemberPermissions.Edit
                              ? "You don't have permission to edit team members"
                              : ""
                          }
                          arrow
                        >
                          <span>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: { xs: "0.65rem", sm: "1rem", md: "1.125rem", lg: "1.125rem", xl: "1.125rem" } }} />}
                          sx={{
                            borderRadius: "1rem",
                            fontWeight: "bold",
                            textTransform: "capitalize",
                            backgroundColor: "primary.main",
                            color: "common.white",
                            padding: { xs: "2px 4px", sm: "6px 10px", md: "8px 12px", lg: "8px 12px", xl: "8px 12px" },
                            fontSize: { xs: "0.55rem", sm: "0.75rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem" },
                            minWidth: { xs: "50px", sm: "70px", md: "80px", lg: "80px", xl: "80px" },
                            "& .MuiButton-startIcon": {
                              marginRight: { xs: "1px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
                              marginLeft: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
                              "& > *:nth-of-type(1)": {
                                fontSize: { xs: "0.65rem", sm: "1rem", md: "1.125rem", lg: "1.125rem", xl: "1.125rem" },
                              },
                            },
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                                "&.Mui-disabled": {
                                  backgroundColor: "rgba(255, 255, 255, 0.12)",
                                  color: "rgba(255, 255, 255, 0.3)",
                                },
                          }}
                              disabled={!TeamMemberPermissions.Edit}
                          onClick={async () => {
                            if (!TeamMemberPermissions.Edit) return;
                            const { blocked, message } = await checkSubscriptionActive(getAuthToken);
                            if (blocked) { toast.error(message, { id: 'subscription-blocked', duration: 5000 }); return; }
                            setSelectedRole(member.Roles.roleName);
                            setSelectedId(member._id);
                            setShowChangeRoleModal(true);
                          }}
                        >
                          {roleName}
                        </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ padding: { xs: "4px 2px", sm: "10px 6px", md: "12px 8px", lg: "14px 10px", xl: "14px 10px" } }}>
                      {roleName === "Owner" ? (
                        <Tooltip title="You can't delete an Owner" arrow>
                          <span>
                            {isXs ? (
                              <IconButton
                                color="error"
                                size="small"
                                disabled
                                sx={{
                                  padding: { xs: "2px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: "0.875rem", sm: "1.25rem", md: "1.5rem", lg: "1.5rem", xl: "1.5rem" },
                                  },
                                  "&.Mui-disabled": {
                                    color: "rgba(255, 255, 255, 0.3)",
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{
                                  borderColor: "error.main",
                                  color: "error.main",
                                  borderRadius: "20px",
                                  textTransform: "capitalize",
                                  fontWeight: "bold",
                                  padding: { xs: "2px 4px", sm: "6px 10px", md: "8px 12px", lg: "8px 12px", xl: "8px 12px" },
                                  fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem" },
                                  minWidth: { xs: "40px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
                                  "&:hover": {
                                    backgroundColor: "error.main",
                                    color: "common.white",
                                    borderColor: "error.main",
                                  },
                                  "&.Mui-disabled": {
                                    borderColor: "rgba(255, 255, 255, 0.12)",
                                    color: "rgba(255, 255, 255, 0.3)",
                                  },
                                }}
                                disabled
                              >
                                Delete
                              </Button>
                            )}
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            !TeamMemberPermissions.Delete
                              ? "You don't have permission to delete team members"
                              : ""
                          }
                          arrow
                        >
                          <span>
                            {isXs ? (
                              <IconButton
                                color="error"
                                size="small"
                                disabled={!TeamMemberPermissions.Delete}
                                onClick={() => handleDelete(member._id)}
                                sx={{
                                  padding: { xs: "2px", sm: "4px", md: "4px", lg: "4px", xl: "4px" },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: { xs: "0.875rem", sm: "1.25rem", md: "1.5rem", lg: "1.5rem", xl: "1.5rem" },
                                  },
                                  "&:hover": {
                                    backgroundColor: "error.main",
                                    color: "common.white",
                                  },
                                  "&.Mui-disabled": {
                                    color: "rgba(255, 255, 255, 0.3)",
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            ) : (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{
                                  borderColor: "error.main",
                                  color: "error.main",
                                  borderRadius: "20px",
                                  textTransform: "capitalize",
                                  fontWeight: "bold",
                                  padding: { xs: "2px 4px", sm: "6px 10px", md: "8px 12px", lg: "8px 12px", xl: "8px 12px" },
                                  fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.8rem", lg: "0.8rem", xl: "0.8rem" },
                                  minWidth: { xs: "40px", sm: "60px", md: "70px", lg: "70px", xl: "70px" },
                                  "&:hover": {
                                    backgroundColor: "error.main",
                                    color: "common.white",
                                    borderColor: "error.main",
                                  },
                                  "&.Mui-disabled": {
                                    borderColor: "rgba(255, 255, 255, 0.12)",
                                    color: "rgba(255, 255, 255, 0.3)",
                                  },
                                }}
                                disabled={!TeamMemberPermissions.Delete}
                                onClick={() => handleDelete(member._id)}
                              >
                                Delete
                              </Button>
                            )}
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination and rows per page */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: { xs: "0.25rem", sm: "0.5rem", md: "0.5rem", lg: "0.75rem", xl: "0.75rem" },
          marginBottom: { xs: "0px", sm: "0px", md: "0px", lg: "0px", xl: "0px" },
          paddingTop: { xs: "0.5rem", sm: "0", md: "0", lg: "0", xl: "0" },
          paddingBottom: { xs: "0px", sm: "0", md: "0", lg: "0", xl: "0" },
          width: "100%",
          color: "text.secondary",
          flexShrink: 0,
          gap: { xs: 0.25, sm: 1, md: 1, lg: 1, xl: 1 },
        }}
      >
        <Typography 
          variant="body2"
          sx={{
            fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" },
          }}
        >
          {indexOfFirstMember + 1}-{" "}
          {Math.min(indexOfLastMember, teamMembers.length)} of{" "}
          {teamMembers.length}
        </Typography>
        <FormControl
          size="small"
          sx={{
            "& .MuiInputLabel-root": {
              color: "text.secondary",
              fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" },
            },
            "& .MuiInputBase-root": {
              color: "text.primary",
              fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" },
              minHeight: { xs: "20px", sm: "28px", md: "30px", lg: "36px", xl: "36px" },
              padding: { xs: "0px 4px", sm: "2px 8px", md: "3px 10px", lg: "4px 12px", xl: "4px 12px" },
            },
            minWidth: { xs: "65px", sm: "100px", md: "110px", lg: "120px", xl: "120px" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
            "& .MuiSelect-icon": {
              color: "text.secondary",
              fontSize: { xs: "0.75rem", sm: "1rem", md: "1.1rem", lg: "1.25rem", xl: "1.25rem" },
            },
          }}
        >
          <InputLabel sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>Rows per page</InputLabel>
          <Select
            value={membersPerPage}
            onChange={handleRowsPerPageChange}
            label="Rows per page"
            sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}
          >
            <MenuItem value={5} sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>5</MenuItem>
            <MenuItem value={10} sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>10</MenuItem>
            <MenuItem value={25} sx={{ fontSize: { xs: "0.55rem", sm: "0.65rem", md: "0.7rem", lg: "0.75rem", xl: "0.75rem" } }}>25</MenuItem>
          </Select>
        </FormControl>
        <Box display="flex" sx={{ gap: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 } }}>
          <IconButton
            onClick={() => handlePageChange(-1)}
            disabled={currentPage === 1}
            size="small"
            sx={{
              color: (theme) => theme.palette.mode === "dark" 
                ? "text.secondary" 
                : "rgba(0, 0, 0, 0.6)",
              padding: { xs: "0px", sm: "2px", md: "3px", lg: "4px", xl: "4px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "0.75rem", sm: "1rem", md: "1.1rem", lg: "1.25rem", xl: "1.25rem" },
              },
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.08)" 
                  : "rgba(0, 0, 0, 0.04)",
                color: (theme) => theme.palette.mode === "dark" 
                  ? "text.secondary" 
                  : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-disabled": {
                color: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.3)" 
                  : "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={() => handlePageChange(1)}
            disabled={indexOfLastMember >= teamMembers.length}
            size="small"
            sx={{
              color: (theme) => theme.palette.mode === "dark" 
                ? "text.secondary" 
                : "rgba(0, 0, 0, 0.6)",
              padding: { xs: "0px", sm: "2px", md: "3px", lg: "4px", xl: "4px" },
              "& .MuiSvgIcon-root": {
                fontSize: { xs: "0.75rem", sm: "1rem", md: "1.1rem", lg: "1.25rem", xl: "1.25rem" },
              },
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.08)" 
                  : "rgba(0, 0, 0, 0.04)",
                color: (theme) => theme.palette.mode === "dark" 
                  ? "text.secondary" 
                  : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-disabled": {
                color: (theme) => theme.palette.mode === "dark" 
                  ? "rgba(255, 255, 255, 0.3)" 
                  : "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Change Role Modal */}
      <Dialog
        open={showChangeRoleModal}
        onClose={() => setShowChangeRoleModal(false)}
        fullWidth
        PaperProps={{
          sx: (t) => ({
            backgroundColor: t.palette.background.paper,
            color: t.palette.text.primary,
            backgroundImage: "none",
            boxShadow:
              t.palette.mode === "dark"
                ? "0 12px 48px rgba(0, 0, 0, 0.65)"
                : "0 8px 24px rgba(0, 0, 0, 0.18)",
          }),
        }}
      >
        <ChangeRole
          currentRole={selectedRole}
          memberId={selectedId}
          onClose={() => setShowChangeRoleModal(false)}
          onChange={handleRoleChange}
        />
      </Dialog>

      <Dialog
        open={showAddMember}
        onClose={handleBackFromAddMember}
        keepMounted
        fullWidth
        PaperProps={{
          sx: (t) => ({
            backgroundColor: t.palette.background.paper,
            color: t.palette.text.primary,
            backgroundImage: "none",
            boxShadow:
              t.palette.mode === "dark"
                ? "0 12px 48px rgba(0, 0, 0, 0.65)"
                : "0 8px 24px rgba(0, 0, 0, 0.18)",
          }),
        }}
      >
        <AddTeamMembers onClose={handleBackFromAddMember} open={showAddMember} />
      </Dialog>

      <Snackbar
        open={addMemberToast.open}
        autoHideDuration={4000}
        onClose={() => setAddMemberToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setAddMemberToast((s) => ({ ...s, open: false }))}
          severity={addMemberToast.severity}
          sx={{ width: "100%" }}
        >
          {addMemberToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamMembers;
